// ═══════════════════════════════════════════════════════════════
// POLLEN CARD — My Portfolio
// Path: src/components/dashboard/pollen/PollenPortfolio.jsx
//
// v2.0.0 — Portfolio Moves workflow
// Cycle: Cultivate → Propose → Acknowledge → Thank → Report → Renew
// Shows current move + days-in-stage for each portfolio donor.
// Sorted by lifetime giving (descending).
// Advance button (next in sequence) or dropdown (jump to any stage).
// Only the assigned director can advance moves.
// Every move auto-creates a portfolio_move contact log entry.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Briefcase,
  X,
  Clock,
  ChevronRight,
  ArrowDownAZ,
  Mail,
  Phone,
  DollarSign,
} from "lucide-react";
import { useApp } from "../../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../../constants/appConfig";
import { supabase } from "../../../lib/supabase";
import { contactLogService } from "../../../services/contactLogService";
import { MOVE_TASK_MAP } from "../../../constants/taskTypes";

// ── Portfolio Move Cycle ────────────────────────────────────────

const MOVES = [
  { id: "cultivate", label: "Cultivate", color: "#f59e0b" },
  { id: "propose", label: "Propose", color: "#8b5cf6" },
  { id: "acknowledge", label: "Acknowledge", color: "#10b981" },
  { id: "thank", label: "Thank", color: "#ec4899" },
  { id: "report", label: "Report", color: "#3b82f6" },
  { id: "renew", label: "Renew", color: "#ef4444" },
];

const MOVE_INDEX = Object.fromEntries(MOVES.map((m, i) => [m.id, i]));

const getMoveConfig = (moveId) =>
  MOVES.find((m) => m.id === moveId) || MOVES[0];

const getNextMove = (currentMove) => {
  const idx = MOVE_INDEX[currentMove] ?? 0;
  return MOVES[(idx + 1) % MOVES.length];
};

// ── Helpers ─────────────────────────────────────────────────────

const formatDaysInStage = (moveDate) => {
  if (!moveDate) return "—";
  const days = Math.floor(
    (Date.now() - new Date(moveDate).getTime()) / (1000 * 60 * 60 * 24)
  );
  if (days === 0) return "Today";
  if (days === 1) return "1d";
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
};

const fmtCurrency = (val) => {
  if (!val || val <= 0) return "$0";
  return `$${val.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

// ═══════════════════════════════════════════════════════════════

export const PollenPortfolio = ({ onClose }) => {
  const {
    palette,
    members,
    directors,
    userProfile,
    navigate,
    updateMember,
    showMessage,
    addTask,
  } = useApp();

  const [selectedDirector, setSelectedDirector] = useState("");
  const [lifetimeTotals, setLifetimeTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [advancing, setAdvancing] = useState(null); // memberId being advanced

  // Is the current user the selected director?
  const currentUserName = useMemo(() => {
    if (!userProfile) return "";
    return userProfile.full_name || userProfile.email || "";
  }, [userProfile]);

  const currentUserEmail = useMemo(() => {
    return userProfile?.email?.toLowerCase() || "";
  }, [userProfile]);

  const isOwnPortfolio = useMemo(() => {
    if (!selectedDirector || !directors?.length) return false;
    const dir = directors.find(
      (d) => d.name?.toLowerCase() === selectedDirector.toLowerCase()
    );
    if (!dir) return false;
    // Match on email first (most reliable), fall back to name
    if (currentUserEmail && dir.email?.toLowerCase() === currentUserEmail) return true;
    if (currentUserName && dir.name?.toLowerCase() === currentUserName.toLowerCase()) return true;
    return false;
  }, [currentUserName, currentUserEmail, selectedDirector, directors]);

  // Auto-select current user's portfolio
  useEffect(() => {
    if (selectedDirector) return;
    if (!directors?.length) return;
    // Try email match first, then name match
    const emailMatch = currentUserEmail
      ? directors.find((d) => d.email?.toLowerCase() === currentUserEmail)
      : null;
    const nameMatch = currentUserName
      ? directors.find((d) => d.name?.toLowerCase() === currentUserName.toLowerCase())
      : null;
    const match = emailMatch || nameMatch;
    if (match) {
      setSelectedDirector(match.name);
    } else if (directors.length > 0) {
      setSelectedDirector(directors[0].name);
    }
  }, [currentUserName, currentUserEmail, directors, selectedDirector]);

  // Portfolio members (exclude deceased)
  const portfolioMembers = useMemo(() => {
    if (!selectedDirector) return [];
    return members.filter(
      (m) =>
        !m.isDeceased &&
        m.assignedDirector &&
        m.assignedDirector.trim().toLowerCase() ===
          selectedDirector.trim().toLowerCase()
    );
  }, [members, selectedDirector]);

  // Fetch lifetime giving totals
  const loadLifetimeTotals = useCallback(async () => {
    if (portfolioMembers.length === 0) {
      setLifetimeTotals({});
      return;
    }
    setLoading(true);
    try {
      const ids = portfolioMembers.map((m) => m.id);
      const chunkSize = 50;
      const giftMap = {};

      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        const { data, error } = await supabase
          .from("gifts")
          .select("member_id, amount")
          .in("member_id", chunk);

        if (!error && data) {
          data.forEach((row) => {
            giftMap[row.member_id] =
              (giftMap[row.member_id] || 0) + (parseFloat(row.amount) || 0);
          });
        }
      }
      setLifetimeTotals(giftMap);
    } catch (err) {
      console.error("PollenPortfolio loadLifetimeTotals", err);
    }
    setLoading(false);
  }, [portfolioMembers]);

  useEffect(() => {
    loadLifetimeTotals();
  }, [loadLifetimeTotals]);

  // Enrich and sort by lifetime giving descending
  const sortedMembers = useMemo(() => {
    return [...portfolioMembers]
      .map((m) => ({
        ...m,
        lifetimeGiving: lifetimeTotals[m.id] || 0,
        moveConfig: getMoveConfig(m.portfolioMove),
      }))
      .sort((a, b) => b.lifetimeGiving - a.lifetimeGiving);
  }, [portfolioMembers, lifetimeTotals]);

  // ── Move advancement ──────────────────────────────────────────

  const advanceMove = useCallback(
    async (member, targetMoveId) => {
      if (!isOwnPortfolio) return;

      const fromMove = getMoveConfig(member.portfolioMove || "cultivate");
      const toMove = getMoveConfig(targetMoveId);
      if (fromMove.id === toMove.id) return;

      setAdvancing(member.id);
      try {
        // Update member record
        await updateMember(
          member.id,
          {
            portfolioMove: toMove.id,
            portfolioMoveDate: new Date().toISOString(),
          },
          true // silent — we'll show our own message
        );

        // Auto-create contact log
        await contactLogService.create(member.id, {
          type: "portfolio_move",
          subject: `${fromMove.label} → ${toMove.label}`,
          notes: `Portfolio move: ${fromMove.label} → ${toMove.label}`,
          date: new Date().toISOString(),
        });

        showMessage(`${member.firstName} ${member.lastName}: ${fromMove.label} → ${toMove.label}`);

        // Auto-complete previous move's task + create new task
        try {
          const { taskService } = await import("../../../services/taskService");
          // Complete any open auto-tasks for the previous move
          await taskService.autoCompleteByMove(member.id, fromMove.id);
          // Create new auto-task for the target move
          const template = MOVE_TASK_MAP[toMove.id];
          if (template && addTask) {
            const donorName = `${member.firstName || ""} ${member.lastName || ""}`.trim();
            const dueDate = new Date();
            dueDate.setDate(dueDate.getDate() + template.dueDays);
            await addTask({
              memberId: member.id,
              directorName: selectedDirector,
              title: template.title.replace("{donor}", donorName),
              category: template.category,
              priority: "normal",
              dueDate: dueDate.toISOString().split("T")[0],
              isAuto: true,
              sourceMove: toMove.id,
            });
          }
        } catch (taskErr) {
          // Task creation is non-critical — don't fail the move
          console.warn("Auto-task creation error", taskErr);
        }
      } catch (err) {
        console.error("advanceMove error", err);
        showMessage("Error advancing move", "error");
      }
      setAdvancing(null);
    },
    [isOwnPortfolio, updateMember, showMessage, addTask, selectedDirector]
  );

  // Don't render if no directors
  if (!directors || directors.length === 0) return null;

  return (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          borderBottom: "none",
          background: palette.background,
          borderRadius: "10px 10px 0 0",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Briefcase size={ICON_SIZES.sm} style={{ color: "#fff" }} />
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 700,
              color: "#fff",
              fontFamily: FONT_FAMILY,
            }}
          >
            My Portfolio
          </span>
          <span
            style={{
              fontSize: FONT_SIZES.xs,
              color: "rgba(255,255,255,0.7)",
              fontFamily: FONT_FAMILY,
              fontStyle: "italic",
            }}
          >
            — Moves Management
          </span>
        </div>
        <button
          onClick={onClose}
          title="Remove card"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: 6,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "rgba(255,255,255,0.6)",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#fca5a5")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.6)")}
        >
          <X size={ICON_SIZES.sm} />
        </button>
      </div>

      {/* Director selector + move legend */}
      <div
        style={{
          padding: "8px 14px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          borderBottom: `1px solid ${palette.cardBorder}`,
          flexWrap: "wrap",
        }}
      >
        <select
          value={selectedDirector}
          onChange={(e) => setSelectedDirector(e.target.value)}
          style={{
            flex: 1,
            minWidth: 120,
            padding: "5px 8px",
            borderRadius: 6,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
            border: `1px solid ${palette.inputBorder}`,
            background: palette.inputBg,
            color: palette.inputColor,
            outline: "none",
            cursor: "pointer",
          }}
        >
          {(directors || []).map((d) => (
            <option key={d.id || d.name} value={d.name}>
              {d.name} (
              {
                members.filter(
                  (m) =>
                    m.assignedDirector?.trim()?.toLowerCase() ===
                    d.name?.toLowerCase()
                ).length
              }
              )
            </option>
          ))}
        </select>
        {/* Move cycle legend */}
        <div style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {MOVES.map((move) => (
            <span
              key={move.id}
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
                padding: "2px 5px",
                borderRadius: 4,
                background: `${move.color}18`,
                color: move.color,
              }}
            >
              {move.label}
            </span>
          ))}
        </div>
      </div>

      {/* Member list */}
      <div
        style={{
          maxHeight: 400,
          overflowY: "auto",
          padding: "4px 6px 6px",
        }}
      >
        {loading ? (
          <div
            style={{
              padding: 16,
              textAlign: "center",
              fontSize: FONT_SIZES.xs,
              color: palette.text3,
              fontFamily: FONT_FAMILY,
              fontStyle: "italic",
            }}
          >
            Loading portfolio...
          </div>
        ) : sortedMembers.length === 0 ? (
          <div
            style={{
              padding: 16,
              textAlign: "center",
              fontSize: FONT_SIZES.xs,
              color: palette.text3,
              fontFamily: FONT_FAMILY,
            }}
          >
            No donors assigned
          </div>
        ) : (
          sortedMembers.map((member) => {
            const move = member.moveConfig;
            const nextMove = getNextMove(member.portfolioMove || "cultivate");
            const isAdvancing = advancing === member.id;

            return (
              <div
                key={member.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "7px 10px",
                  borderRadius: 6,
                  marginBottom: 1,
                  transition: "background 0.15s",
                  opacity: isAdvancing ? 0.5 : 1,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = `${palette.accent1}06`)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                {/* Name + org (clickable) */}
                <div
                  style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                  onClick={() => navigate("memberDetail", member)}
                >
                  <div
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontWeight: 600,
                      color: palette.text1,
                      fontFamily: FONT_FAMILY,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {member.lastName}, {member.firstName}
                    {member.donorType === "household" &&
                      member.householdName && (
                        <span
                          style={{
                            fontWeight: 400,
                            color: palette.text3,
                            fontSize: FONT_SIZES.xs,
                            marginLeft: 6,
                          }}
                        >
                          ({member.householdName})
                        </span>
                      )}
                  </div>
                  {member.organization && (
                    <div
                      style={{
                        fontSize: FONT_SIZES.xs,
                        color: palette.text3,
                        fontFamily: FONT_FAMILY,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {member.organization}
                    </div>
                  )}
                  {/* Contact shortcuts */}
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      marginTop: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    {member.email && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 3,
                          fontSize: FONT_SIZES.xs,
                          color: palette.text3,
                          fontFamily: FONT_FAMILY,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`mailto:${member.email}`);
                        }}
                        title={member.email}
                      >
                        <Mail size={ICON_SIZES.xs} />
                        {member.email}
                      </span>
                    )}
                    {member.phone && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 3,
                          fontSize: FONT_SIZES.xs,
                          color: palette.text3,
                          fontFamily: FONT_FAMILY,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`tel:${member.phone}`);
                        }}
                        title={member.phone}
                      >
                        <Phone size={ICON_SIZES.xs} />
                        {member.phone}
                      </span>
                    )}
                  </div>
                </div>

                {/* Lifetime giving */}
                <div
                  style={{
                    flexShrink: 0,
                    marginLeft: 8,
                    textAlign: "right",
                    minWidth: 52,
                  }}
                >
                  <span
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontWeight: 700,
                      color:
                        member.lifetimeGiving > 0 ? "#10b981" : palette.text3,
                      fontFamily: FONT_FAMILY,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmtCurrency(member.lifetimeGiving)}
                  </span>
                </div>

                {/* Current move badge + days in stage */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    flexShrink: 0,
                    marginLeft: 8,
                    minWidth: 80,
                  }}
                >
                  <span
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontWeight: 700,
                      fontFamily: FONT_FAMILY,
                      padding: "2px 6px",
                      borderRadius: 4,
                      background: `${move.color}18`,
                      color: move.color,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {move.label}
                  </span>
                  <span
                    style={{
                      fontSize: FONT_SIZES.xs,
                      color: palette.text3,
                      fontFamily: FONT_FAMILY,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatDaysInStage(member.portfolioMoveDate)}
                  </span>
                </div>

                {/* Advance controls — only for own portfolio */}
                {isOwnPortfolio && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      flexShrink: 0,
                      marginLeft: 6,
                    }}
                  >
                    {/* Advance to next button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        advanceMove(member, nextMove.id);
                      }}
                      disabled={isAdvancing}
                      title={`Advance to ${nextMove.label}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        padding: "3px 7px",
                        borderRadius: 5,
                        border: "none",
                        fontSize: FONT_SIZES.xs,
                        fontWeight: 600,
                        fontFamily: FONT_FAMILY,
                        cursor: isAdvancing ? "wait" : "pointer",
                        background: `${nextMove.color}20`,
                        color: nextMove.color,
                      }}
                    >
                      <ChevronRight size={ICON_SIZES.xs} />
                      {nextMove.label}
                    </button>

                    {/* Dropdown to jump to any stage */}
                    <select
                      value={member.portfolioMove || "cultivate"}
                      onChange={(e) => {
                        e.stopPropagation();
                        advanceMove(member, e.target.value);
                      }}
                      disabled={isAdvancing}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        padding: "2px 2px",
                        borderRadius: 4,
                        border: `1px solid ${palette.inputBorder}`,
                        background: palette.inputBg,
                        color: palette.inputColor,
                        fontSize: FONT_SIZES.xs,
                        fontFamily: FONT_FAMILY,
                        cursor: isAdvancing ? "wait" : "pointer",
                        width: 20,
                        appearance: "none",
                        WebkitAppearance: "none",
                        textAlign: "center",
                      }}
                      title="Jump to stage"
                    >
                      {MOVES.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
